import { useCallback } from "react";

import {
  type QueryParamConfig,
  useQueryParam,
  withDefault,
} from "use-query-params";

import { CustomJsonCompressedParam } from "@north-beam/nb-common";

import { type Dimensions } from "../charts/chart";
import { useTabTopPlatforms } from "../table/contexts";
import { type TabId } from "../tabs";

import { useActiveTab } from "./use-active-tab";

export type Atomicity = {
  count: number;
  atomicity: string;
};

export type AdKeyInfo = {
  adKey: string;
  dimensions: Dimensions;
};

export type FiltersState = {
  inclusionAtomicities: Atomicity[];
  exclusionAtomicities: Atomicity[];
  inclusionAdKeys: AdKeyInfo[];
  exclusionAdKeys: AdKeyInfo[];

  campaignInclusionAdKeys?: AdKeyInfo[];
  campaignExclusionAdKeys?: AdKeyInfo[];
  adsetInclusionAdKeys?: AdKeyInfo[];
  adsetExclusionAdKeys?: AdKeyInfo[];
};
export type TabState = {
  proposedFiltersState: FiltersState;
  appliedFiltersState?: FiltersState;

  selectAll: boolean;
  totalCount: number;
  preselectionDone?: boolean;
  isPreselectionFinished?: boolean;
};

export const defaultTabState = {
  selectAll: false,
  totalCount: 0,
  proposedFiltersState: {
    inclusionAtomicities: [],
    inclusionAdKeys: [],
    exclusionAtomicities: [],
    exclusionAdKeys: [],
  },
} as TabState;

const TabStateParam: QueryParamConfig<TabState> = withDefault(
  CustomJsonCompressedParam<TabState>(),
  defaultTabState,
);

export const useActiveTabState = () => {
  const activeTab = useActiveTab();
  const tab = useTabState(activeTab.activeTab);
  return { ...activeTab, ...tab } as const;
};

export const useTabState = (tab: TabId) => {
  const [tabState, setTabState] = useQueryParam(
    `tab-state-${tab}`,
    withDefault(TabStateParam, defaultTabState),
    { updateType: "pushIn" },
  );

  const [, setTopPlatforms] = useTabTopPlatforms(tab);
  const updateTabState = useCallback(
    (value: TabState | ((newTabState: TabState) => TabState)) =>
      setTabState(value, "pushIn"),
    [setTabState],
  );

  const unsetTabState = useCallback(() => {
    updateTabState(defaultTabState);
    setTopPlatforms([]);
  }, [setTopPlatforms, updateTabState]);

  return {
    tabState,
    unsetTabState,
    setTabState: updateTabState,
  };
};
